import React, { useState, useEffect } from 'react';
import { MessageSquare, Phone, ExternalLink, Loader2, Info, ChevronDown, Building2 } from 'lucide-react';

const STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']
];

const partyTone = party => {
  if (!party) return 'bg-gray-100 text-gray-700';
  if (party.startsWith('Democrat')) return 'bg-blue-100 text-blue-800';
  if (party.startsWith('Republican')) return 'bg-red-100 text-red-800';
  return 'bg-purple-100 text-purple-800';
};

const MemberCard = ({ member, expanded, detail, loadingDetail, onToggle }) => (
  <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-100/50 overflow-hidden">
    <button
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex items-center gap-4 p-6 text-left hover:bg-blue-50/40 transition-all"
    >
      {member.imageUrl ? (
        <img
          src={member.imageUrl}
          alt=""
          className="w-14 h-14 rounded-2xl object-cover bg-gray-100 shrink-0"
          onError={e => { e.target.style.visibility = 'hidden'; }}
        />
      ) : (
        <div className="w-14 h-14 rounded-2xl bg-gray-100 shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-gray-900 text-lg leading-tight truncate">{member.name}</h3>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`px-2.5 py-0.5 rounded-xl text-xs font-semibold ${partyTone(member.party)}`}>
            {member.party || 'Unknown'}
          </span>
          <span className="text-sm text-gray-500">
            {member.district === null ? 'U.S. Senator' : `District ${member.district}`}
          </span>
        </div>
      </div>

      <ChevronDown
        size={20}
        className={`text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
      />
    </button>

    {expanded && (
      <div className="px-6 pb-6 border-t border-gray-100 pt-5">
        {loadingDetail ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading contact details...</span>
          </div>
        ) : !detail ? (
          <p className="text-sm text-red-600">Could not load contact details right now.</p>
        ) : (
          <div className="space-y-3">
            {detail.phone && (
              <a
                href={`tel:${detail.phone.replace(/[^\d]/g, '')}`}
                className="flex items-center gap-3 bg-blue-50 hover:bg-blue-100 p-4 rounded-2xl transition-all"
              >
                <Phone size={18} className="text-blue-600 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">{detail.phone}</p>
                  <p className="text-xs text-gray-500">Washington, D.C. office</p>
                </div>
              </a>
            )}

            {detail.office && (
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50">
                <Building2 size={18} className="text-gray-400 shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{detail.office}</p>
              </div>
            )}

            {detail.website && (
              <a
                href={detail.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-2xl font-semibold hover:bg-blue-700 transition-all"
              >
                <span>Official website</span>
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);

const RepsContent = () => {
  const [state, setState] = useState(() => {
    try { return window.localStorage.getItem('civicspark.state') || 'FL'; } catch { return 'FL'; }
  });
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setExpanded(null);
    fetch(`/api/members?state=${state}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.source === 'congress.gov') { setData(d); setStatus('ok'); }
        else setStatus(d.source === 'unavailable' ? 'unconfigured' : 'error');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [state]);

  const changeState = value => {
    setState(value);
    try { window.localStorage.setItem('civicspark.state', value); } catch { /* not persisted */ }
  };

  const toggle = async member => {
    if (expanded === member.bioguideId) { setExpanded(null); return; }
    setExpanded(member.bioguideId);
    if (details[member.bioguideId]) return;
    setLoadingDetail(member.bioguideId);
    try {
      const res = await fetch(`/api/members/${member.bioguideId}`);
      const d = res.ok ? await res.json() : null;
      setDetails(prev => ({ ...prev, [member.bioguideId]: d }));
    } catch {
      setDetails(prev => ({ ...prev, [member.bioguideId]: null }));
    } finally {
      setLoadingDetail(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-white/20 rounded-2xl">
            <MessageSquare size={24} />
          </div>
          <h2 className="text-3xl font-bold">Contact Your Representatives</h2>
        </div>
        <p className="opacity-90 text-lg">Every current member of Congress for your state</p>
      </div>

      <div className="bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-lg border border-gray-100/50">
        <label htmlFor="state-select" className="block text-sm font-semibold text-gray-700 mb-2">
          Your state
        </label>
        <select
          id="state-select"
          value={state}
          onChange={e => changeState(e.target.value)}
          className="w-full md:w-72 p-4 rounded-2xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none font-medium text-gray-800 bg-white"
        >
          {STATES.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-3">
          We ask for your state, never your home address. To find your exact House district,
          use the official lookup at house.gov.
        </p>
      </div>

      {status === 'loading' && (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="text-blue-600 animate-spin mb-3" size={32} />
          <p className="text-gray-500">Loading members of Congress...</p>
        </div>
      )}

      {status === 'unconfigured' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-2xl flex items-start gap-3">
          <Info size={20} className="shrink-0 mt-0.5" />
          <p className="text-sm">
            <strong>Member lookup is not configured.</strong> Set the CONGRESS_API_KEY config var.
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl">
          Could not reach congress.gov right now. Please try again shortly.
        </div>
      )}

      {status === 'ok' && data && (
        <>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              U.S. Senators <span className="text-gray-400 font-medium">({data.senators.length})</span>
            </h3>
            <div className="space-y-4">
              {data.senators.map(m => (
                <MemberCard
                  key={m.bioguideId}
                  member={m}
                  expanded={expanded === m.bioguideId}
                  detail={details[m.bioguideId]}
                  loadingDetail={loadingDetail === m.bioguideId}
                  onToggle={() => toggle(m)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              U.S. Representatives <span className="text-gray-400 font-medium">({data.representatives.length})</span>
            </h3>
            <div className="space-y-4">
              {data.representatives.map(m => (
                <MemberCard
                  key={m.bioguideId}
                  member={m}
                  expanded={expanded === m.bioguideId}
                  detail={details[m.bioguideId]}
                  loadingDetail={loadingDetail === m.bioguideId}
                  onToggle={() => toggle(m)}
                />
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-3xl p-6">
            <h4 className="font-bold text-gray-900 mb-2">Calling for the first time?</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Staff log the issue and whether you were for or against it, not your argument.
              Say your name and that you live in the state or district, name the bill or issue,
              say what you want them to do, and thank them. A call takes under a minute, and
              you do not need to be old enough to vote to make one.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default RepsContent;
